import React, { useState, useEffect, useCallback } from "react";
import { Box, Grid, Typography } from "@mui/material";

import BusinessIcon from "@mui/icons-material/Business";
import PageContainer from "../layouts/PageContainer";
import AppButton from "../components/buttons/AppButton";
import BranchCard from "../components/branches/BranchCard";
import CreateBranchModal from "../components/branches/CreateBranchModal";
import UpdateBranchModal from "../components/branches/UpdateBranchModal";
import ViewBranchModal from "../components/branches/ViewBranchModal";
import ConfirmDialog from "../components/common/ConfirmDialog";
import AppCircularProgress from "../components/common/AppCircularProgress";

import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { getBranches, getBranchById } from "../redux/actions/branchActions";
import { branchActions } from "../redux/slices/branch";
import PageError from "../components/common/PageError";
import { BranchSummaryDto } from "../shared/dtos/branch/BranchSummaryDto";

const BranchesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: branches,
    item: branch,
    loadingFetchAll: loadingBranches,
    loadingFetchOne: loadingBranch,
    errorFetchAll: errorLoadingBranches,
    errorFetchOne: errorLoadingBranch,
    serverStatus,
    loadingCreate: loadingBranchCreate,
    errorCreate: errorCreatingBranch,
    loadingUpdate: loadingBranchUpdate,
    errorUpdate: errorUpdatingBranch,
    loadingDelete: loadingBranchDelete,
  } = useAppSelector((state) => state.branch);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedBranch, setSelectedBranch] = useState<BranchSummaryDto | null>(
    null,
  );

  const canCreate = hasPermission("branch:create");
  const canUpdate = hasPermission("branch:update");
  const canDelete = hasPermission("branch:delete");
  const canView = hasPermission("branch:read");

  const fetchBranches = useCallback(() => {
    dispatch(getBranches());
  }, [dispatch]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback(() => {
    if (!loadingBranchCreate) setIsCreateModalOpen(false);
  }, [loadingBranchCreate]);

  const handleOpenUpdate = (b: BranchSummaryDto): void => {
    dispatch(getBranchById(b.id));
    setSelectedBranch(b);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback(() => {
    if (!loadingBranchUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedBranch(null);
    }
  }, [loadingBranchUpdate]);

  const handleOpenView = (b: BranchSummaryDto): void => {
    dispatch(getBranchById(b.id));
    setIsViewModalOpen(true);
  };
  const handleCloseView = (): void => setIsViewModalOpen(false);

  const handleOpenDelete = (b: BranchSummaryDto): void => {
    setSelectedBranch(b);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback(() => {
    if (!loadingBranchDelete) {
      setIsDeleteModalOpen(false);
      setSelectedBranch(null);
    }
  }, [loadingBranchDelete]);

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;
    if (operation === "create" && status === 201) handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();
    dispatch(branchActions.setServerStatus(null));
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  const renderContent = (): React.JSX.Element => {
    if (loadingBranches) return <AppCircularProgress />;
    if (errorLoadingBranches)
      return <PageError error={errorLoadingBranches} onRetry={fetchBranches} />;

    if (!branches || branches.length === 0) {
      return (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography>No Branches Found</Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={3}>
        {branches.map((b) => (
          <Grid key={b.id} size={{ xs: 12, sm: 6, md: 4 }}>
            <BranchCard
              branch={b}
              onView={canView ? () => handleOpenView(b) : undefined}
              onEdit={canUpdate ? () => handleOpenUpdate(b) : undefined}
              onDelete={canDelete ? () => handleOpenDelete(b) : undefined}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <PageContainer
      title="Company Branches"
      subtitle="Manage all campus locations and their details."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<BusinessIcon />}
            onClick={handleOpenCreate}
          >
            Add New Branch
          </AppButton>
        )
      }
    >
      {renderContent()}

      {isCreateModalOpen && (
        <CreateBranchModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingBranchCreate}
          error={errorCreatingBranch}
        />
      )}
      {isUpdateModalOpen && selectedBranch && branch && (
        <UpdateBranchModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          branch={branch.id === selectedBranch.id ? branch : null}
          loading={loadingBranchUpdate || loadingBranch}
          error={errorUpdatingBranch}
        />
      )}
      {isViewModalOpen && (
        <ViewBranchModal
          open={isViewModalOpen}
          onClose={handleCloseView}
          branch={branch}
          loading={loadingBranch}
          error={errorLoadingBranch}
        />
      )}
      {isDeleteModalOpen && selectedBranch && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={() => {}}
          title="Delete Branch"
          description={`Are you sure you want to delete the branch "${selectedBranch.name}"?`}
          loading={loadingBranchDelete}
        />
      )}
    </PageContainer>
  );
};

export default BranchesPage;
